import { DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import type { DragEndEvent } from '@dnd-kit/core';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Form } from 'antd';
import classNames from 'classnames';
import { useMemo } from 'react';

import { RedirectLink } from 'app/components/commons/RedirectLink/RedirectLink';
import { CoverCellRoom } from 'app/components/lists/cells';
import { useGetRoomsByHotelId } from 'app/hooks/data/core/admin/hotels/useHotelRooms';
import { useRoomCategories } from 'app/hooks/data/rooms/useRoomCategories';
import type { PackRoomResponse } from 'app/typings/core/admin/packs/pack';
import { ROOM_STATUS } from 'app/typings/core/admin/rooms/enum/roomStatus';
import { pluralize } from 'app/utils/strings';

type SortableRoomProps = {
  packRoomResponse: PackRoomResponse;
  isEditMode: boolean;
  hasBorder: boolean;
};

const SortableRoom = ({
  packRoomResponse,
  isEditMode,
  hasBorder,
}: SortableRoomProps) => {
  const form = Form.useFormInstance();
  const formValues = Form.useWatch([], form) ?? {};
  const { data: hotelRooms } = useGetRoomsByHotelId(formValues.hotelId);
  const { data: roomCategories } = useRoomCategories();

  const room = hotelRooms?.rooms.find(
    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    (room) => room.id === packRoomResponse.roomId
  );
  const roomCategory = roomCategories?.find(
    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    (roomCategory) => roomCategory.id === room?.categoryId
  );

  const firstPicture =
    room?.pictures && room.pictures.length > 0 ? room.pictures[0] : undefined;
  const coverPicture = firstPicture
    ? {
        id: firstPicture.id,
        createdAt: firstPicture.updatedAt
          ? new Date(firstPicture.updatedAt)
          : new Date(),
        updatedAt: firstPicture.updatedAt
          ? new Date(firstPicture.updatedAt)
          : new Date(),
        path: firstPicture.path,
        dimensions: firstPicture.dimensions,
      }
    : undefined;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: packRoomResponse.roomId });
  const style = { transform: CSS.Transform.toString(transform), transition };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();

    const current = form.getFieldValue('packRooms') || [];

    const updated = current.filter(
      (packRoom: PackRoomResponse) =>
        packRoom.roomId !== packRoomResponse.roomId
    );

    form.setFieldsValue({ packRooms: updated });
    form.validateFields();
  };

  return (
    <div
      ref={setNodeRef}
      className={classNames({
        'er-item-wrapper': hasBorder,
      })}
      style={style}
      {...attributes}
    >
      <div className="er-item">
        <div className="er-item-main">
          <div className="er-item-left">
            <CoverCellRoom
              category={roomCategory?.name ?? ''}
              entity={{
                coverPicture,
                name: room?.name,
              }}
            />
          </div>

          <div className="er-item-right">
            <span />
            <span />
            <div className="er-value">
              {pluralize(room?.roomCapacity.guest, 'guest', 'guests')}
              {room?.roomCapacity?.baby
                ? ` + ${pluralize(room.roomCapacity.baby, 'baby', 'babies')}`
                : ''}
            </div>
            <div className="er-status">
              <span
                className={`er-dot ${
                  room?.status === ROOM_STATUS.PUBLISHED
                    ? 'published'
                    : 'unpublished'
                }`}
              />
              {room?.status === ROOM_STATUS.PUBLISHED
                ? 'Published'
                : 'Unpublished'}
            </div>
            <div className="er-item-buttons">
              <div className="er-buttons-actions">
                {isEditMode ? (
                  <DeleteOutlined onClick={handleDelete} />
                ) : (
                  <span />
                )}
                <RedirectLink href={`/rooms/${room?.id}`} />
              </div>
              {isEditMode ? (
                <HolderOutlined className="er-drag-handle" {...listeners} />
              ) : (
                <span className="er-drag-handle-placeholder" />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

type SortableRoomsListProps = {
  rooms: PackRoomResponse[];
  isEditMode: boolean;
  onSortEnd: ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => void;
};

export const SortableRoomsList = ({
  rooms,
  isEditMode,
  onSortEnd,
}: SortableRoomsListProps) => {
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = rooms.findIndex((r) => r.roomId === active.id);
      const newIndex = rooms.findIndex((r) => r.roomId === over.id);

      onSortEnd({ oldIndex, newIndex });
    }
  };

  const ids = useMemo(() => rooms.map((r) => r.roomId), [rooms]);

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {rooms.map((room, idx) => (
          <SortableRoom
            key={room.roomId}
            packRoomResponse={room}
            isEditMode={isEditMode}
            hasBorder={idx !== 0}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};
